import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo2 from '../assets/logo2.png';
import {
  PlayCircle,
  Youtube,
  Sparkles,
  ClipboardCheck,
  CreditCard,
  Network,
  Presentation,
  BarChart3,
  Users,
  Award,
  ChevronRight,
  Check,
  Menu,
  X,
  BookOpen,
  Building2,
  GraduationCap,
  Briefcase,
  HeartHandshake,
  Zap,
  ArrowRight,
} from 'lucide-react';

/* ─── Dados ─────────────────────────────────────────── */
const FEATURES = [
  { icon: CreditCard,    label: 'Flashcards',           desc: 'Memorização ativa gerada por IA' },
  { icon: ClipboardCheck,label: 'Quiz automático',       desc: 'Avaliações criadas do vídeo' },
  { icon: Network,       label: 'Mapa Mental',           desc: 'Visualização do conteúdo' },
  { icon: Presentation,  label: 'Slides',                desc: 'Apresentações geradas automaticamente' },
  { icon: BarChart3,     label: 'Infográfico',           desc: 'Resumo visual do tema' },
  { icon: BookOpen,      label: 'Trilhas de aprendizado',desc: 'Jornadas por setor' },
  { icon: Users,         label: 'Painel administrativo', desc: 'Gestão completa da equipe' },
  { icon: Award,         label: 'Certificados',          desc: 'Emissão digital automática' },
];

const PROFILES = [
  { icon: Building2,    label: 'Empresas de TI',       desc: 'Treine sua equipe técnica com cursos personalizados por área.' },
  { icon: GraduationCap,label: 'Equipes de Estágio',   desc: 'Integre estagiários com trilhas estruturadas e avaliações.' },
  { icon: Briefcase,    label: 'Setor Comercial',      desc: 'Capacite o time de vendas com conteúdo relevante e certificado.' },
  { icon: HeartHandshake,label: 'RH e Treinamento',   desc: 'Centralize todos os treinamentos em uma única plataforma.' },
];

const PLANS = [
  {
    name: 'Starter',
    price: 'R$ 197',
    period: '/mês',
    highlight: false,
    features: [
      'Até 10 usuários',
      'Até 5 cursos ativos',
      'Flashcards, Quiz e Mapa Mental',
      'Painel administrativo básico',
    ],
  },
  {
    name: 'Profissional',
    price: 'R$ 497',
    period: '/mês',
    highlight: true,
    badge: 'Mais popular',
    features: [
      'Até 50 usuários',
      'Cursos ilimitados',
      'Todos os recursos de IA',
      'Certificados digitais',
      'Trilhas por setor',
      'Exportação de relatórios CSV',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Sob consulta',
    period: '',
    highlight: false,
    features: [
      'Usuários ilimitados',
      'SSO Google e Microsoft',
      'White-label (logo e cores)',
      'Suporte prioritário',
      'Gerente de conta dedicado',
    ],
  },
];

/* ─── Componente principal ───────────────────────────── */
const HeroPage = () => {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="lp-root">
      {/* ── Navbar ── */}
      <nav className="lp-nav">
        <div className="lp-container lp-nav-inner">
          <img src={Logo2} alt="Escola de Suporti" className="lp-logo" />

          {/* desktop links */}
          <div className="lp-nav-links">
            <a href="#como-funciona" className="lp-nav-link">Como funciona</a>
            <a href="#funcionalidades" className="lp-nav-link">Funcionalidades</a>
            <a href="#planos" className="lp-nav-link">Planos</a>
            <Link to="/login" className="lp-btn lp-btn-outline">Entrar</Link>
            <Link to="/login" className="lp-btn lp-btn-primary">Começar grátis</Link>
          </div>

          {/* mobile hamburger */}
          <button className="lp-hamburger" onClick={() => setNavOpen(v => !v)} aria-label="Menu">
            {navOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* mobile drawer */}
        {navOpen && (
          <div className="lp-mobile-drawer">
            <a href="#como-funciona" className="lp-drawer-link" onClick={() => setNavOpen(false)}>Como funciona</a>
            <a href="#funcionalidades" className="lp-drawer-link" onClick={() => setNavOpen(false)}>Funcionalidades</a>
            <a href="#planos" className="lp-drawer-link" onClick={() => setNavOpen(false)}>Planos</a>
            <Link to="/login" className="lp-btn lp-btn-outline lp-drawer-btn" onClick={() => setNavOpen(false)}>Entrar</Link>
            <Link to="/login" className="lp-btn lp-btn-primary lp-drawer-btn" onClick={() => setNavOpen(false)}>Começar grátis</Link>
          </div>
        )}
      </nav>

      {/* ── SEÇÃO 1: Hero ── */}
      <section className="lp-hero">
        <div className="lp-container lp-hero-inner">
          <div className="lp-hero-text">
            <span className="lp-badge-pill">
              <Zap size={14} /> Powered by IA
            </span>
            <h1 className="lp-hero-title">
              Transforme qualquer vídeo do YouTube em um curso completo <span className="lp-highlight">em minutos</span>
            </h1>
            <p className="lp-hero-sub">
              A plataforma que usa IA para gerar flashcards, quiz, mapa mental e slides automaticamente para sua equipe — sem esforço manual.
            </p>
            <div className="lp-hero-btns">
              <Link to="/login" className="lp-btn lp-btn-primary lp-btn-lg">
                Começar agora gratuitamente <ArrowRight size={18} />
              </Link>
              <a href="#como-funciona" className="lp-btn lp-btn-ghost lp-btn-lg">
                <PlayCircle size={20} /> Ver demonstração
              </a>
            </div>
            <p className="lp-hero-note">✓ 14 dias grátis &nbsp;·&nbsp; ✓ Sem cartão de crédito &nbsp;·&nbsp; ✓ Cancele quando quiser</p>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-mockup-card">
              <div className="lp-mockup-header">
                <span className="lp-mockup-dot lp-dot-red" />
                <span className="lp-mockup-dot lp-dot-yellow" />
                <span className="lp-mockup-dot lp-dot-green" />
                <span className="lp-mockup-url">escola.drsuporti.com.br</span>
              </div>
              <div className="lp-mockup-body">
                <div className="lp-mockup-label"><Youtube size={14} /> Vídeo do YouTube detectado</div>
                <div className="lp-mockup-progress-row">
                  <span>Gerando flashcards…</span><span className="lp-orange">87%</span>
                </div>
                <div className="lp-mockup-bar"><div className="lp-mockup-fill" style={{ width: '87%' }} /></div>
                <div className="lp-mockup-chips">
                  <span className="lp-chip lp-chip-green"><Check size={10} /> Quiz pronto</span>
                  <span className="lp-chip lp-chip-green"><Check size={10} /> Mapa Mental</span>
                  <span className="lp-chip lp-chip-blue"><Sparkles size={10} /> Slides</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SEÇÃO 2: Como funciona ── */}
      <section id="como-funciona" className="lp-section lp-section-alt">
        <div className="lp-container">
          <h2 className="lp-section-title">Como funciona</h2>
          <p className="lp-section-sub">3 passos para transformar qualquer vídeo em um curso completo</p>

          <div className="lp-steps">
            {[
              { n: '01', icon: Youtube,      title: 'Cole o link do YouTube', desc: 'Basta colar a URL do vídeo que deseja transformar em curso. Nossa plataforma faz o resto.' },
              { n: '02', icon: Sparkles,     title: 'A IA gera o curso automaticamente', desc: 'Em minutos são criados flashcards, quiz, mapa mental, slides e infográfico prontos para uso.' },
              { n: '03', icon: Award,        title: 'Sua equipe aprende e recebe certificado', desc: 'Alunos acessam, fazem o quiz e recebem certificado digital ao concluir o curso.' },
            ].map(({ n, icon: Icon, title, desc }) => (
              <div key={n} className="lp-step">
                <div className="lp-step-num">{n}</div>
                <div className="lp-step-icon-wrap"><Icon size={28} /></div>
                <h3 className="lp-step-title">{title}</h3>
                <p className="lp-step-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEÇÃO 3: Funcionalidades ── */}
      <section id="funcionalidades" className="lp-section">
        <div className="lp-container">
          <h2 className="lp-section-title">Tudo que sua equipe precisa</h2>
          <p className="lp-section-sub">Cada vídeo gera automaticamente 5 tipos de conteúdo educacional</p>

          <div className="lp-features-grid">
            {FEATURES.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="lp-feature-card">
                <div className="lp-feature-icon"><Icon size={26} /></div>
                <h4 className="lp-feature-label">{label}</h4>
                <p className="lp-feature-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEÇÃO 4: Para quem é ── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-container">
          <h2 className="lp-section-title">Para quem é</h2>
          <p className="lp-section-sub">Ideal para empresas que precisam treinar equipes de forma rápida e eficiente</p>

          <div className="lp-profiles-grid">
            {PROFILES.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="lp-profile-card">
                <div className="lp-profile-icon"><Icon size={30} /></div>
                <h4 className="lp-profile-label">{label}</h4>
                <p className="lp-profile-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEÇÃO 5: Planos ── */}
      <section id="planos" className="lp-section">
        <div className="lp-container">
          <h2 className="lp-section-title">Planos e Preços</h2>
          <p className="lp-section-sub">Comece gratuitamente por 14 dias, sem cartão de crédito</p>

          <div className="lp-plans-grid">
            {PLANS.map(plan => (
              <div key={plan.name} className={`lp-plan-card ${plan.highlight ? 'lp-plan-highlight' : ''}`}>
                {plan.badge && <span className="lp-plan-badge">{plan.badge}</span>}
                <h3 className="lp-plan-name">{plan.name}</h3>
                <div className="lp-plan-price">
                  {plan.price}<span className="lp-plan-period">{plan.period}</span>
                </div>
                <ul className="lp-plan-features">
                  {plan.features.map(f => (
                    <li key={f} className="lp-plan-feature">
                      <Check size={14} className="lp-check" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/login"
                  className={`lp-btn lp-plan-btn ${plan.highlight ? 'lp-btn-primary' : 'lp-btn-outline-dark'}`}
                >
                  {plan.price === 'Sob consulta' ? 'Falar com consultor' : 'Começar grátis'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEÇÃO 6: CTA Final ── */}
      <section className="lp-cta">
        <div className="lp-container lp-cta-inner">
          <h2 className="lp-cta-title">Pronto para transformar o treinamento da sua equipe?</h2>
          <p className="lp-cta-sub">Junte-se a empresas que já economizam horas criando cursos com IA.</p>
          <Link to="/login" className="lp-btn lp-btn-primary lp-btn-xl">
            Criar conta grátis <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <img src={Logo2} alt="Escola de Suporti" className="lp-footer-logo" />
          <p className="lp-footer-copy">
            Desenvolvido por <strong>Dr Suporti</strong> © 2025 — Todos os direitos reservados.
          </p>
          <div className="lp-footer-links">
            <a href="#planos">Planos</a>
            <a href="#funcionalidades">Funcionalidades</a>
            <Link to="/login">Entrar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HeroPage;
